import depsloader from "../../../depsloader";

const GEOCODER_SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const GEOCODER_REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const GEOCODER_TIMEOUT = 20000;

// Maps-Key wird serverseitig ermittelt und als Datenattribut an <html> übergeben.
function mapsKey() {
  const ds = document.documentElement.dataset;
  return ds && ds.mapsKey ? ds.mapsKey : "";
}

function str(v) {
  if (typeof v === "string") {
    return v.trim();
  }
  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }
  return "";
}

function validLatLng(lat, lng) {
  return typeof lat === "number" && typeof lng === "number" &&
    isFinite(lat) && isFinite(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

// Ersatz für Antworten ohne properties.type (alter Photon-Adapter).
function legacyType(p) {
  const key = str(p.osm_key);
  const value = str(p.osm_value);

  if (key === "highway") {
    return "street";
  }
  if (key === "boundary" || (key === "place" && ["suburb", "neighbourhood", "quarter", "borough"].indexOf(value) > -1)) {
    return "district";
  }
  if (key === "place" && ["city", "town", "village", "municipality"].indexOf(value) > -1) {
    return "city";
  }
  if (key === "place" && ["hamlet", "isolated_dwelling", "locality"].indexOf(value) > -1) {
    return "locality";
  }
  if (str(p.housenumber)) {
    return "house";
  }
  return "";
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.reverseRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (g.label && !g.street) {
      p.push(g.label);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature in eine Geoadresse um, ohne das Feature zu verändern.
  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const p = l.properties;
    const lng = Number(l.geometry.coordinates[0]);
    const lat = Number(l.geometry.coordinates[1]);

    if (!validLatLng(lat, lng)) {
      return null;
    }

    const name = str(p.name);
    const type = str(p.type).toLowerCase() || legacyType(p);
    const geoaddress = {};

    const street = str(p.street);
    const housenumber = str(p.housenumber);
    const postcode = str(p.postcode);
    const city = str(p.city);
    const district = str(p.district);

    if (street) {
      geoaddress["street"] = street;
      if (housenumber) {
        geoaddress["number"] = housenumber;
      }
    }
    if (postcode) {
      geoaddress["zip"] = postcode;
    }
    if (city) {
      geoaddress["city"] = city;
    }
    if (district) {
      geoaddress["district"] = district;
    }

    switch (type) {
      case "street":
        if (!geoaddress["street"] && name) {
          geoaddress["street"] = name;
        }
        break;
      case "city":
        if (name) {
          geoaddress["city"] = name;
        }
        break;
      case "district":
        if (!geoaddress["district"] && name && name !== geoaddress["city"]) {
          geoaddress["district"] = name;
        }
        break;
      case "locality":
        if (!geoaddress["district"] && name && name !== geoaddress["city"]) {
          geoaddress["district"] = name;
        }
        break;
      default:
        // house, county, state, country und unbekannte Typen
        break;
    }

    // Ortsteil mit gleichem Namen wie der Ort nicht doppelt ausgeben.
    if (geoaddress["district"] && geoaddress["district"] === geoaddress["city"]) {
      delete geoaddress["district"];
    }

    // Fehlt Straße und Ort, den Namen nur als Beschriftung übernehmen.
    if (name && !geoaddress["street"] && !geoaddress["city"] && !geoaddress["district"]) {
      geoaddress["label"] = name;
    } else if (name && type === "house" && !geoaddress["street"]) {
      geoaddress["label"] = name;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    if (str(p.state)) {
      geoaddress["state"] = str(p.state);
    }
    if (str(p.country)) {
      geoaddress["country"] = str(p.country);
    }

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  _features(data) {
    const results = [];

    if (data && Array.isArray(data.features)) {
      data.features.forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });
    }

    return results;
  }

  // Abgebrochene Anfragen enden mit einer leeren Liste statt offen zu bleiben.
  geocode(params, request) {
    return new Promise((resolve, reject) => {
      this.axios.get(GEOCODER_SEARCH_URL, {
        cancelToken: request.source.token,
        timeout: GEOCODER_TIMEOUT,
        params
      }).then(res => {
        resolve(this._features(res.data));
      }).catch((thrown) => {
        if (this.axios.isCancel(thrown)) {
          resolve([]);
        } else {
          reject(thrown);
        }
      }).finally(() => {
        if (this.axiosRequest === request) {
          this.axiosRequest = null;
        }
      });
    });
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const key = mapsKey();
    const query = str(q);
    if (!key || !query) {
      return Promise.resolve([]);
    }

    const CancelToken = this.axios.CancelToken;
    const request = {
      source: CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.geocode(params, request);
  }

  // Liefert die Geoadresse oder null (kein Key, ungültige Koordinaten,
  // Fehler, kein Treffer oder durch eine neuere Anfrage abgebrochen).
  reverse(lat, lng) {
    const latNum = Number(lat);
    const lngNum = Number(lng);
    const key = mapsKey();

    if (this.reverseRequest != null) {
      this.reverseRequest.cancel();
      this.reverseRequest = null;
    }

    if (!key || !validLatLng(latNum, lngNum)) {
      return Promise.resolve(null);
    }

    const source = this.axios.CancelToken.source();
    this.reverseRequest = source;

    const params = new URLSearchParams();
    params.append("lat", String(latNum));
    params.append("lon", String(lngNum));
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(GEOCODER_REVERSE_URL, {
      cancelToken: source.token,
      timeout: GEOCODER_TIMEOUT,
      params
    }).then(res => {
      const results = this._features(res.data);
      return results.length > 0 ? results[0] : null;
    }).catch(err => {
      if (!this.axios.isCancel(err)) {
        console.warn("Reverse-Geocoding fehlgeschlagen", err && err.message ? err.message : err);
      }
      return null;
    }).finally(() => {
      if (this.reverseRequest === source) {
        this.reverseRequest = null;
      }
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };
